/**
 * lucide-init.js — Self-hosted Lucide icon bundle + auto-hydration.
 *
 * Built with bundle:true (see build-assets.mjs BUNDLED_ENTRIES) so the
 * lucide npm package is inlined into the output.
 *
 * Load with <script defer src="{{ static_dist('lucide-init.js') }}"> — defer
 * ensures the DOM is parsed before DOMContentLoaded fires.
 *
 * Usage in templates:
 *   <i data-lucide="arrow-right"></i>
 *   Any such element is replaced with an inline SVG on hydration.
 *
 * Re-hydration (for dynamically inserted icons):
 *   window.Lucide.createIcons({ icons: window.Lucide.icons });
 */
import { createIcons, icons } from 'lucide';

// Expose globally so templates and other scripts can trigger re-hydration.
window.Lucide = { createIcons, icons };

/**
 * Translate a legacy Bootstrap Icons class (e.g. "bi-journal-text") or a
 * raw Lucide name into a Lucide kebab-case name. Existing DB rows still
 * hold "bi-*" values for things like column.icon, so we coalesce at the
 * render boundary instead of doing a destructive migration. Pass-through
 * for already-Lucide names; fall back to "circle" on null/undefined.
 *
 *   window.coalesceLucideName('bi-bug')      // → 'bug'
 *   window.coalesceLucideName('bi-journal-text') // → 'book-open'
 *   window.coalesceLucideName('alert-triangle')  // → 'alert-triangle'
 */
const BI_TO_LUCIDE = {
  'bi-journal-text': 'book-open', 'bi-check-square': 'check-square',
  'bi-bug': 'bug', 'bi-star': 'star', 'bi-inbox': 'inbox',
  'bi-play-circle': 'play-circle', 'bi-clock': 'clock',
  'bi-check-circle': 'check-circle', 'bi-gear': 'settings', 'bi-eye': 'eye',
  'bi-calendar-check': 'calendar-check', 'bi-arrow-right-circle': 'arrow-right-circle',
  'bi-pause-circle': 'pause-circle', 'bi-exclamation-triangle': 'alert-triangle',
  'bi-shield-check': 'shield-check', 'bi-clipboard-check': 'clipboard-check',
  'bi-lightning': 'zap', 'bi-flag': 'flag', 'bi-target': 'target',
  'bi-rocket': 'rocket', 'bi-rocket-takeoff': 'rocket', 'bi-tools': 'wrench',
  'bi-wrench': 'wrench', 'bi-cpu': 'cpu', 'bi-diagram-3': 'git-fork',
  'bi-layers': 'layers', 'bi-kanban': 'kanban-square', 'bi-circle': 'circle',
  'bi-hourglass-split': 'hourglass', 'bi-hourglass': 'hourglass',
  'bi-pencil-square': 'square-pen', 'bi-code-square': 'code-2',
  'bi-fire': 'flame', 'bi-trophy': 'trophy', 'bi-gem': 'gem', 'bi-gift': 'gift',
  'bi-heart': 'heart', 'bi-chat-dots': 'message-circle', 'bi-bell': 'bell',
  'bi-question-circle': 'help-circle', 'bi-info-circle': 'info',
  'bi-hammer': 'hammer', 'bi-brush': 'brush', 'bi-palette': 'palette',
  'bi-image': 'image', 'bi-camera': 'camera', 'bi-film': 'film',
  'bi-music-note': 'music', 'bi-headphones': 'headphones',
  'bi-book': 'book-open', 'bi-journal': 'notebook', 'bi-newspaper': 'newspaper',
  'bi-file-text': 'file-text', 'bi-file-earmark-text': 'file-text',
  'bi-file-earmark': 'file', 'bi-clipboard': 'clipboard', 'bi-calendar': 'calendar',
  'bi-clock-history': 'history', 'bi-stopwatch': 'timer', 'bi-alarm': 'alarm-clock',
  'bi-battery-charging': 'battery-charging', 'bi-database': 'database',
  'bi-server': 'server', 'bi-cloud': 'cloud', 'bi-wifi': 'wifi',
  'bi-phone': 'smartphone', 'bi-laptop': 'laptop', 'bi-display': 'monitor',
  'bi-printer': 'printer', 'bi-mouse': 'mouse', 'bi-keyboard': 'keyboard',
  'bi-usb-drive': 'usb', 'bi-hdd': 'hard-drive', 'bi-box': 'package',
  'bi-archive': 'archive', 'bi-briefcase': 'briefcase', 'bi-backpack': 'backpack',
  'bi-bag': 'shopping-bag', 'bi-cart': 'shopping-cart',
  'bi-basket': 'shopping-basket', 'bi-wallet': 'wallet',
  'bi-credit-card': 'credit-card', 'bi-cash': 'dollar-sign',
  'bi-piggy-bank': 'piggy-bank', 'bi-graph-up': 'trending-up',
  'bi-pie-chart': 'pie-chart', 'bi-bar-chart': 'bar-chart',
  'bi-list-ul': 'list', 'bi-list-ol': 'list-ordered', 'bi-folder': 'folder',
  'bi-tag': 'tag', 'bi-bookmark': 'bookmark', 'bi-tags': 'tags',
};

window.coalesceLucideName = function coalesceLucideName(name) {
  if (!name) return 'circle';
  if (BI_TO_LUCIDE[name]) return BI_TO_LUCIDE[name];
  // strip leading "bi-" and use as-is; if it was already a Lucide name, pass through.
  return name.startsWith('bi-') ? name.slice(3) : name;
};

/**
 * Escape an untrusted string for safe interpolation into HTML text or
 * attribute context. Use when building markup via template literals +
 * innerHTML where the value comes from a fetch response or DB row
 * (column titles, attachment names, user-typed content). Pre-existing
 * server-side autoescape covers Jinja-rendered paths; this is for the
 * JS render boundary.
 *
 *   el.innerHTML = `<strong>${window.escapeHtml(column.title)}</strong>`;
 */
window.escapeHtml = function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

/**
 * Swap an icon inside a host element. Replaces the host's innerHTML with a
 * fresh `<i data-lucide=…>` placeholder and re-hydrates. Use this for
 * dynamic icon swaps (theme toggles, password show/hide, expand/collapse),
 * where the placeholder has already been replaced with an <svg> and you
 * need to put a different icon in.
 *
 *   window.swapIcon(button, 'eye-off');
 *   window.swapIcon(chevronWrap, 'chevron-down', { extraClasses: 'ts-chevron' });
 */
window.swapIcon = function swapIcon(host, lucideName, opts) {
  if (!host) return;
  const o = opts || {};
  const cls = ['inline-block', 'h-4', 'w-4', o.extraClasses || ''].join(' ').trim();
  host.innerHTML = '<i data-lucide="' + lucideName + '" class="' + cls + '" aria-hidden="true"></i>';
  createIcons({ icons });
};

// Initial hydration: replace all <i data-lucide="..."> present at load time.
//
// MPE-532: this pass is load-bearing again. The icon sprite is a hot subset
// (repeating card/row/macro icons only — see scripts/build-icon-sprite.mjs),
// so every page one-off arrives here as a placeholder. Measured on a
// 540-card board: ~280 placeholders, a constant that does NOT scale with card
// count, versus ~2.8k sprite <use> references that never touch this code.
// If this number ever starts tracking row count, a per-card icon has fallen
// out of the sprite (tests/test_icon_sprite_budget.py guards exactly that).
document.addEventListener('DOMContentLoaded', () => {
  createIcons({ icons });
});

// MutationObserver: catch dynamically-inserted <i data-lucide="..."> elements
// from Alpine, fetch responses, or innerHTML assignments.
//
// Debounced via setTimeout(≤50ms) so rapid bursts coalesce. We also
// short-circuit when no added node IS or CONTAINS an i[data-lucide]
// placeholder — without this guard the observer fires for every Alpine
// transition class flip and re-runs createIcons in a tight loop, which
// in turn mutates the DOM and re-triggers the observer. On a page with
// any active animation that loop saturates the main thread and the page
// stops responding to input.
let _pendingHydration = null;

function _mutationTouchesLucide(mutations) {
  for (const m of mutations) {
    for (const node of m.addedNodes) {
      if (node.nodeType !== 1) continue; // skip text / comment
      if (node.matches && node.matches('i[data-lucide]')) return true;
      if (node.querySelector && node.querySelector('i[data-lucide]')) return true;
    }
  }
  return false;
}

const observer = new MutationObserver((mutations) => {
  if (_pendingHydration !== null) return; // Already scheduled.
  if (!_mutationTouchesLucide(mutations)) return;

  _pendingHydration = setTimeout(() => {
    _pendingHydration = null;
    createIcons({ icons });
  }, 50);
});

observer.observe(document.body, { childList: true, subtree: true });
